import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { f2 } from '../../copyFlow2'
import { v3 } from '../../copyV3'
import type { Card } from '../../lib/browse'
import { loadSaved, savedTrace, savedVerdict, saveItem, settleSaved, unsaveItem, useIsSaved, useSavedKnown } from '../../lib/saved'
import { useBrowsePlan } from '../../lib/browse'
import { ConsentLine } from './BrowseTray'
import { isUnderTheRadar, normalizePrice } from '../../lib/format'
import { atMidnight, clock, currentZone, dayShort, laYmd, type PlanItemData } from '../../lib/plan'
import { cleanTitle, ownStart, ownWeekly } from '../../lib/fit'
import { r3t, weekdaysText } from '../../copyR3Times'
import { buttonClass } from '../../ui/styles'
import { CategoryLabel } from '../../ui/CategoryLabel'
import { Tag } from '../../ui/Chip'
import { Heart } from '../../ui/icons'
import { Spinner } from '../../ui/Spinner'
import { Photo } from './Photo'
import { inSomeGroup } from '../../lib/inGroup'
import { TasteActions } from './TasteActions'
import { tf } from '../../copyTaste'
import { justHid } from '../../lib/tasteFeedback'
import { reloadOnce } from '../../lib/chunkReload'
import { inCollection } from '../../lib/collections'
import { useFlag } from '../../lib/flags'
import { fb } from '../../copyFeatBrowse'
import { firstRunBackTo, firstRunOwed } from '../../lib/v2'
import { dropEarlyBrowse } from '../../lib/earlyBrowse'

// R-29 Add to <group>: its code and copy load only for someone in a group (F5: not in the first download)
const GroupAdd = lazy(reloadOnce(() => import('../GroupAdd').then((m) => ({ default: m.GroupAdd }))))

const MI_PER_KM = 0.621371

/** "Sat, Oct 3 · 7:30 PM" for an event (no time when the source gave none). One already under way that ends later says so
 *  instead of a past start (QA prod r1, B-044: CRUISE LA read "Sat, Oct 3" on Oct 8): "On now · Through Sun, Oct 11", or
 *  "On now · Until 9:00 PM" when it ends today. */
export function whenLine(card: Pick<Card, 'when' | 'item'>, now = Date.now()): string | null {
  let start = card.when?.start ?? (card.item.kind === 'event' ? card.item.start_at : null)
  if (!start) return null
  // R-98 (B-115, Round 3 lane 1): the event's own time. A stored AM/PM slip its own words correct is shown corrected; a show
  // stored at 7 AM with nothing to confirm it shows its day and "Check the time", never the wrong time.
  let timeOk = true
  if (card.item.kind === 'event' && start === card.item.start_at) {
    const own = ownStart(card.item, currentZone())
    if (own) { start = own.at; timeOk = own.time || atMidnight(own.at) }
  }
  const end = card.when?.end ?? (card.item.kind === 'event' ? card.item.end_at : null)
  if (end && Date.parse(start) <= now && Date.parse(end) > now) {
    const endDay = laYmd(end)
    if (endDay === laYmd(new Date(now))) return f2('listing.on_now_until', { time: clock(end) })
    // an end at midnight belongs to the day before ("through Sat" for a run ending Sun 00:00)
    return f2('listing.on_now_through', { day: dayShort(atMidnight(end) ? laYmd(new Date(Date.parse(end) - 60_000)) : endDay) })
  }
  const day = dayShort(laYmd(start))
  if (!timeOk) return `${day} · ${r3t('r3t.time_unclear')}`
  return atMidnight(start) ? day : `${day} · ${clock(start)}`
}

/** R-98 (B-137, Round 3 lane 1): a weekly night at a venue ("Wednesday CraftNight at Akbar") says its own day and time
 *  ("Every Wednesday · Check the time on their page"), not the bar's "Daily 4pm-2am". null = its hours describe it. */
export function weeklyLine(item: PlanItemData): string | null {
  const w = ownWeekly(item)
  if (!w) return null
  const days = weekdaysText(w.days)
  return w.hour == null ? `${r3t('r3t.every_day', { days })} · ${r3t('r3t.check_time')}` : r3t('r3t.every_day_time', { days, time: hourClock(w.hour) })
}
/** "8:00 PM" for a decimal hour (19.5 = 7:30 PM), the same style as clock(). */
export const hourClock = (h: number) => {
  const hh = Math.floor(h) % 24, mm = Math.round((h - Math.floor(h)) * 60)
  return `${hh % 12 || 12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`
}

/** The sourced price, "Free", or "Price not listed" (never a guess). */
export function priceText(item: PlanItemData): string {
  if (item.price_text?.trim()) return normalizePrice(item.price_text)
  // B-046: a source that says free is tagged free even where is_free was not set (26 live rows on 10/08/26, B-071)
  if (item.is_free === true || item.cost_tier === 'free' || (item.tags ?? []).includes('free')) return f2('listing.free')
  // Plan v3 blind test r2 (Gabe): a price written in the item's own title or line ("$5 per group") is shown as written
  const said = `${item.title} ${item.one_line ?? ''}`.match(PRICE_IN_TEXT)
  if (said) return normalizePrice(said[0])
  return f2('listing.price_unknown')
}
const PRICE_IN_TEXT = /\$\s?\d+(?:[.,]\d{2})?(?:\s*(?:-|to)\s*\$?\d+(?:[.,]\d{2})?)?(?:\s*(?:per|\/|a)\s*(?:person|group|ticket|car|adult|couple|night|class|session|table|team))?/i

// blind test r3 (Kenji): under a tenth of a mile read 'About 0.0 mi away'
export const milesText = (km: number) => (km * MI_PER_KM < 0.05 ? v3('v3.distance_tiny') : f2('listing.distance', { miles: km * MI_PER_KM < 1 ? (km * MI_PER_KM).toFixed(1) : Math.round(km * MI_PER_KM) }))

/** Where the detail opens: the same page with ?item=, so Back closes it first and the list keeps its place. */
export function useOpenDetail() {
  const loc = useLocation()
  const navigate = useNavigate()
  return (card: Card) => {
    const q = new URLSearchParams(loc.search)
    // B-153 (Alvin pin 562): one activity in the detail at a time. Opening another while one is open swaps it in place
    // (replace), so X and Back close the detail instead of stepping back through earlier activities.
    const swapping = q.has('item')
    q.set('item', card.item.id)
    navigate({ pathname: loc.pathname, search: q.toString() }, { state: { card }, preventScrollReset: true, replace: swapping })
  }
}

/**
 * The card's action (Plan v3, R-73): Save, the one yes. Saved toggles it back out. Save is the old Add underneath (the
 * person's draft in that item's city), so the taste signal is unchanged. Shared with the detail, where it also offers
 * Build around this (blind test r1, Marco) and the consent line before a first save (Tom).
 * Blind test r1 (Tom, Kim): the button keeps one width while saving (a spinner swaps for the heart, the label stays),
 * and an error line takes no room above it, so a quick second tap on the next card lands where it was aimed.
 */
export function ListingActions({ card, compact = false }: { card: Card; compact?: boolean }) {
  const item = card.item
  const navigate = useNavigate()
  const loc = useLocation()
  const st = useIsSaved(item.id, card.added)
  const [err, setErr] = useState(false)
  const [going, setGoing] = useState(false)
  const gone = item.kind === 'event' && !!(item.end_at ?? item.start_at) && new Date(item.end_at ?? item.start_at!).getTime() < Date.now()
  const on = st === 'saved'
  // blind test r3 (Kenji): the first Save tap saves; the consent line shows at the same moment, once per person (never in
  // the hero, never a second tap). B-120: on a card it shows in the Saved bar (fixed, so nothing on the page moves); only the
  // detail sheet's own Save bar (compact, where the Saved bar never shows) keeps it under the button.
  const { consentShown } = useBrowsePlan()
  const [asking, setAsking] = useState(false)
  const [settling, setSettling] = useState(false)
  // B-194: the sheet opened by link asks for the Saved list at once, so its button shows the truth before the first tap
  useEffect(() => { if (compact) void loadSaved({ onlyWithSession: true }).catch(() => {}) }, [compact])
  // B-194 part 3: until a full Saved list has answered, the sheet's button shows busy (it may not know yet); a tap then
  // is kept and acts once the list answers
  const known = useSavedKnown()
  const blind = compact && !known && st !== 'saving'
  // listing first (alvin 10/17): a new visitor let in by a shared link does the first run on the first Save, then lands back here
  // A Save that started the first run is applied when they come back (then=save; tester polish 10/18): one tap, one save.
  const owed = (then?: 'save') => {
    if (!firstRunOwed()) return false
    dropEarlyBrowse() // F5: a Browse answer fetched before the first run must not show after it
    navigate(firstRunBackTo(`/?item=${encodeURIComponent(item.id)}${then ? `&then=${then}` : ''}`))
    return true
  }
  const pending = useRef(compact && new URLSearchParams(loc.search).get('then') === 'save' && new URLSearchParams(loc.search).get('item') === item.id)
  useEffect(() => {
    if (!pending.current || !known) return
    pending.current = false
    const q = new URLSearchParams(loc.search); q.delete('then')
    navigate({ search: `?${q}` }, { replace: true, state: loc.state })
    if (on || st === 'saving' || gone) return // already saved here (another tab, or the list says so): nothing to send
    void saveItem(item).then(() => { if (!consentShown) setAsking(true) }, () => setErr(true))
  }, [known]) // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = () => {
    if (owed('save')) return
    if (justHid()) return // the card under the finger just slid up after a Not for me (lib/tasteFeedback.ts)
    if (settling) return // one tap at a time; the first is already waiting for the list
    setErr(false)
    if (compact && !on && !consentShown) setAsking(true)
    // Act on the settled Saved list. A tap on a shown state the list contradicts sends nothing (the thing already is
    // what the tap asked for; the button now shows it) and says why in the console for the tester's log
    const saw = on
    // a Save never waits on the list (one tap, one add; a 409 means it already was saved, B-194 J2 timing); only an
    // un-save needs the list, for the row to delete
    // B-194 part 4: a sheet that does not know the list yet (busy button) shows no state, so its tap is a plain toggle of
    // whatever the list then says: saved -> remove, not saved -> add; it never fast-paths an add (that was a silent 409)
    const wasBlind = blind
    // the gate's log: what the button showed at the tap (B-194 part 4)
    console.info('[saved] tap ' + JSON.stringify({ item: item.id, shown: saw, busy: wasBlind, sheet: compact, ...savedTrace(item.id) }))
    if (!saw && !wasBlind) { void saveItem(item).catch(() => setErr(true)); return }
    setSettling(true)
    // B-199: every queued tap ends in one logged outcome. The gate saw one tap (WebKit, 1 of 24) wait for the list and then
    // send nothing and log nothing; a tap still waiting after 20 s logs where it stopped, and one stuck before it sent
    // anything acts on what the list says by then (a send already on its way is never sent twice)
    const step = { at: 'settle' as 'settle' | 'remove' | 'add' | 'done' | 'not sent' }
    // B-200: the list could not be read in full and nothing says it is saved: send nothing, say so, and ask again
    const unknown = () => {
      step.at = 'not sent'
      console.warn('[saved] tap not sent: the Saved list could not be read ' + JSON.stringify({ item: item.id, ...savedTrace(item.id) }))
      setErr(true)
      void loadSaved({}, true).catch(() => {})
    }
    const act = (now: boolean) => { step.at = now ? 'remove' : 'add'; return now ? unsaveItem(item.id) : saveItem(item) }
    const watchdog = setTimeout(() => {
      if (step.at === 'done' || step.at === 'not sent') return
      console.warn('[saved] tap stuck ' + JSON.stringify({ item: item.id, at: step.at, ...savedTrace(item.id) }))
      setSettling(false)
      if (step.at !== 'settle') { void loadSaved({}, true).catch(() => {}); return }
      const now = savedVerdict(item.id, card.added)
      if (now === null) { unknown(); return }
      void act(now)
        .then(() => { step.at = 'done'; console.info('[saved] tap done (after the wait) ' + JSON.stringify({ item: item.id, ...savedTrace(item.id) })) })
        .catch(() => setErr(true))
    }, 20_000)
    void settleSaved(item.id, card.added)
      .then((now) => {
        if (step.at !== 'settle') return // the watchdog already acted on this tap
        setSettling(false)
        if (now === null) return unknown()
        if (wasBlind || now === saw) return act(now).then(() => { step.at = 'done'; console.info('[saved] tap done ' + JSON.stringify({ item: item.id, sent: now ? 'remove' : 'add' })) })
        step.at = 'not sent'
        console.warn('[saved] tap not sent: the list disagrees with the button ' + JSON.stringify({ item: item.id, saw, now, ...savedTrace(item.id) }))
      })
      .catch((e) => { console.warn('[saved] tap failed ' + JSON.stringify({ item: item.id, at: step.at, error: String(e), ...savedTrace(item.id) })); setSettling(false); setErr(true) })
      .finally(() => { if (step.at === 'done' || step.at === 'not sent') clearTimeout(watchdog) })
  }
  const around = () => {
    if (owed()) return
    setGoing(true)
    // F5: the Build screens are not in the first download (App.tsx fetches them in the background once Browse is up)
    void reloadOnce(() => import('../../screens/Build'))()
      .then(({ planAround }) => planAround(item, on, (to, justSaved) => navigate(to, { state: { from: loc.pathname + loc.search, justSaved } })))
      .catch(() => { setGoing(false); setErr(true) })
  }
  return (
    <div className={compact ? '' : 'mt-5'}>
      {gone && !on && <p className="t-meta mb-2 text-ink-2">{f2('listing.ended')}</p>}
      <button type="button" data-review-id="save-button" onClick={toggle} disabled={(gone && !on) || settling} aria-pressed={on || st === 'saving'} aria-busy={st === 'saving' || settling || blind || undefined}
        className={buttonClass(on || st === 'saving' ? 'primary' : 'secondary', compact ? 'lg' : 'md', compact ? 'w-full' : 'min-w-[120px]')}>
        {st === 'saving' || settling || blind ? <Spinner size={18} label={false} className="text-current" /> : <Heart size={18} fill={on ? 'currentColor' : 'none'} />}
        {on || st === 'saving' ? v3('v3.saved') : v3('v3.save')}
      </button>
      {(err || st === 'failed') && <p role="alert" className="t-meta mt-1 text-error">{v3('v3.save_error')}</p>}
      {/* one row kept for it from the first paint (B-120: nothing appears under a finger once the groups list arrives) */}
      {!gone && inSomeGroup() && <div className="min-h-12"><Suspense fallback={null}><GroupAdd itemId={item.id} compact={compact} /></Suspense></div>}
      {compact && !gone && (
        <button type="button" data-review-id="plan-around" disabled={going} onClick={around} className={buttonClass('ghost', 'md', 'mt-1 w-full text-ink underline underline-offset-4')}>
          {going && <Spinner size={16} label={false} className="text-current" />}{v3('v3.plan_around')}
        </button>
      )}
      {asking && <ConsentLine force className={compact ? 'mt-2 text-center' : 'mt-2 max-w-[300px]'} />}
    </div>
  )
}

/** A listing (flow3 §1 anatomy): category, full title (opens detail), line, when, where, price, then the actions. */
export function ListingCard({ card, showDistance, className = '', reason = null, more = 0, photo = false, row, position }: { card: Card; showDistance: boolean; className?: string; reason?: string | null; more?: number; photo?: boolean; row?: string; position?: number }) {
  const open = useOpenDetail()
  const item = card.item
  const when = whenLine(card)
  const weekly = when ? null : weeklyLine(item)
  const radar = isUnderTheRadar(item)
  // R-30 (AGENTS.md Purpose: next to other people): the same rule as the Go with others row
  const people = useFlag('go_with_others') && inCollection(item, 'people')
  const place = [item.neighborhood, showDistance && card.km !== null ? milesText(card.km) : null].filter(Boolean).join(' · ')
  return (
    <article data-cat={item.category} data-review-id={`listing:${item.id}`} className={`flex flex-col border-t border-line pt-5 pb-2 ${className}`}>
      {photo && (
        <Link to={`?item=${item.id}`} tabIndex={-1} aria-hidden="true" onClick={(e) => { e.preventDefault(); open(card) }} className="mb-4 block">
          <Photo item={item} />
        </Link>
      )}
      <div className="flex min-h-6 flex-wrap items-center justify-between gap-2">
        <CategoryLabel category={item.category} tags={item.tags} />
        <span className="flex flex-wrap items-center justify-end gap-2">
          {people && <span data-review-id="card-people"><Tag label>{fb('fb.people.card')}</Tag></span>}
          {item.rsvp === 'approval' && <span data-review-id="rsvp-approval" className="t-label border border-line px-1.5 py-0.5 text-ink-2">{f2('listing.rsvp_approval')}</span>}
          {radar && <Tag label tone="accent">{f2('listing.radar')}</Tag>}
        </span>
      </div>
      {/* taste v2: one card in each 8 of For you is from something they have not tried yet; the server flags it */}
      {card.explore && <p data-review-id="card-explore" className="t-label mt-3 inline-flex self-start bg-accent-soft px-2 py-0.5 text-accent-ink">{tf('tf.explore')}</p>}
      {reason && <p data-review-id="card-reason" className="t-label mt-3 inline-flex self-start border border-line px-2 py-0.5 text-ink-2">{reason}</p>}
      <h3 className="mt-3">
        <Link to={`?item=${item.id}`} onClick={(e) => { e.preventDefault(); open(card) }}
          className="t-card text-ink hover:underline decoration-line underline-offset-4">
          {cleanTitle(item.title)}
        </Link>
      </h3>
      {item.one_line && <p className="t-body mt-2 text-ink-2">{item.one_line}</p>}
      <div className="t-meta mt-3 space-y-1 text-ink">
        {when ? (
          <p className="t-time">{when}{more > 0 && <span data-review-id="more-dates" className="t-meta font-normal text-ink-2">{` · ${v3('v3.more_dates', { count: more })}`}</span>}</p>
        ) : (
          // R-98 (B-137): a weekly night's own day before any venue hours (Round 3 lane 1 block)
          weekly ? <p data-review-id="card-weekly" className="t-time">{weekly}</p> :
          <p>{item.hours_text ? <><span className="font-semibold">{f2('listing.regular_hours')}</span> · {item.hours_text}</> : <span className="text-ink-2">{f2('listing.check_hours')}</span>}</p>
        )}
        {place && <p className="text-ink-2">{place}</p>}
        <p className="text-ink-2">{priceText(item)}</p>
      </div>
      <div className="mt-auto">
        <ListingActions card={card} />
        <TasteActions card={card} ctx={{ source: 'card', row, position, explore: card.explore }} />
      </div>
    </article>
  )
}

/** A static placeholder card while listings load (no shimmer: skeletons stay still under reduced motion too). */
export function CardSkeleton({ className = '' }: { className?: string }) {
  return (
    <div aria-hidden="true" className={`border-t border-line pt-5 ${className}`}>
      <div className="h-3 w-24 rounded-full bg-sunk" />
      <div className="mt-4 h-5 w-4/5 rounded-full bg-sunk" />
      <div className="mt-2 h-5 w-3/5 rounded-full bg-sunk" />
      <div className="mt-4 h-3.5 w-full rounded-full bg-sunk" />
      <div className="mt-2 h-3.5 w-2/3 rounded-full bg-sunk" />
      <div className="mt-6 h-[52px] w-full rounded-[12px] bg-sunk" />
    </div>
  )
}
